import styles from "./Static.module.css";

// ── About Us ──────────────────────────────────────────────────────────────────
export function AboutPage() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="container">
          <p className={styles.eyebrow}>About Us</p>
          <h1 className={styles.heading}>The shortcut to knowing.<br />Small but satisfying.</h1>
          <p className={styles.sub}>
            Shortly Life was built for curious, ambitious people in India who want to get up to speed fast — without paying subscription fees or sitting through hour-long video courses.
          </p>
        </div>
      </div>
      <div className="container section-gap-sm">
        <div className={styles.prose}>
          <h2>Our Story</h2>
          <p>
            We started Shortly Life after noticing a gap: there were too many expensive video courses and not enough affordable, high-density written resources. In a world moving at AI speed, you don't need to master everything in depth — you need to know enough about most things to stay ahead, and you need it fast. PDFs are quick to read, easy to reference, and work on any device. So we set out to build the best bite-sized PDF course library in India.
          </p>
          <h2>What We Stand For</h2>
          <ul>
            <li><strong>Small but satisfying.</strong> Every PDF is short, dense, and built to get you results — no fluff.</li>
            <li><strong>Affordable pricing.</strong> Courses start at ₹11. Knowledge is for everyone.</li>
            <li><strong>Instant delivery.</strong> Your PDF is in your inbox the second payment clears.</li>
          </ul>
          <h2>Our Team</h2>
          <p>
            We're a small, passionate team of educators, designers, and developers based in India. Every course is written, reviewed, and tested by subject matter experts before it goes live.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
export function ContactPage() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="container">
          <p className={styles.eyebrow}>Contact Us</p>
          <h1 className={styles.heading}>We'd love to hear from you.</h1>
          <p className={styles.sub}>Got a question, feedback, or a course idea? Drop us a message and we'll respond within 24 hours.</p>
        </div>
      </div>
      <div className="container section-gap-sm">
        <div className={styles.contactGrid}>
          <div className={styles.contactInfo}>
            {[
              { icon: "📧", title: "Email", value: "support@shortly.life", sub: "We reply within 24 hrs" },
              { icon: "📱", title: "WhatsApp", value: "", sub: "Mon–Sat, 10am–6pm" },
              { icon: "📍", title: "Location", value: "Kolkata, West Bengal", sub: "India" },
            ].map((item, i) => (
              <div key={i} className={styles.contactCard}>
                <span className={styles.contactIcon}>{item.icon}</span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.value}</p>
                  <small>{item.sub}</small>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.contactForm}>
            <h3>Send a Message</h3>
            <div className={styles.formFields}>
              <input className={styles.field} placeholder="Your name" />
              <input className={styles.field} placeholder="Your email" type="email" />
              <input className={styles.field} placeholder="Subject" />
              <textarea className={styles.fieldArea} placeholder="Your message..." rows={5} />
              <button className={styles.sendBtn}>Send Message →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Refund Policy ─────────────────────────────────────────────────────────────
export function RefundPage() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="container">
          <p className={styles.eyebrow}>Refund Policy</p>
          <h1 className={styles.heading}>Refunds on Digital Products</h1>
        </div>
      </div>
      <div className="container section-gap-sm">
        <div className={styles.prose}>
          <h2>Digital Goods — Please Read Before You Buy</h2>
          <p>
            Shortly Life sells digital PDF courses that are delivered instantly to your email upon payment. Because our products are downloadable digital goods that cannot be "returned" once accessed, all sales are generally final. We encourage you to review the course description, preview (where available), and FAQs carefully before purchasing.
          </p>
          <h2>When You Are Eligible for a Refund</h2>
          <p>We will issue a full refund if any of the following applies and you contact us within 7 days of purchase:</p>
          <ul>
            <li>You were charged more than once for the same order (duplicate payment).</li>
            <li>The PDF failed to deliver and we are unable to deliver it after you contact us.</li>
            <li>The file is corrupted or unreadable and we cannot provide a working replacement.</li>
            <li>You were charged but the order was never completed on our end.</li>
          </ul>
          <h2>When a Refund Will Not Be Issued</h2>
          <p>To protect against misuse of digital products, refunds will not be granted in these cases:</p>
          <ul>
            <li>Requests made more than 7 days after purchase.</li>
            <li>The PDF has been downloaded, opened, or its password has been used to access the content (delivery is considered complete at this point).</li>
            <li>"Change of mind," or a feeling that the content was too short — our courses are intentionally bite-sized, as described on every product page.</li>
            <li>Claims that the content was already known to you, or did not meet undisclosed personal expectations.</li>
            <li>The PDF has been shared, copied, or distributed in violation of our Terms.</li>
            <li>Buyer's failure to read the course description, level, or table of contents before purchase.</li>
          </ul>
          <h2>How to Request a Refund</h2>
          <p>
            Email <strong>support@shortly.life</strong> with your order ID and the email address used at purchase, along with a short description of the issue. We review every eligible request and, where approved, process refunds within 5–7 business days to the original payment method. Refund timelines after approval depend on your bank or payment provider.
          </p>
          <h2>Questions?</h2>
          <p>If you have any questions about this policy before purchasing, reach out to us at support@shortly.life — we're happy to help you choose the right course.</p>
        </div>
      </div>
    </div>
  );
}

// ── Privacy Policy ────────────────────────────────────────────────────────────
export function PrivacyPage() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="container">
          <p className={styles.eyebrow}>Privacy Policy</p>
          <h1 className={styles.heading}>Your privacy matters to us.</h1>
        </div>
      </div>
      <div className="container section-gap-sm">
        <div className={styles.prose}>
          <h2>Data We Collect</h2>
          <p>We collect your name, email address, and phone number when you make a purchase. This is used solely to deliver your course and process your order.</p>
          <h2>How We Use Your Data</h2>
          <ul>
            <li>To deliver your purchased PDF courses</li>
            <li>To send your invoice and OTP verification</li>
            <li>To provide customer support</li>
            <li>To send occasional product updates (you can opt out anytime)</li>
          </ul>
          <h2>Data Sharing</h2>
          <p>We do not sell, trade, or share your personal data with third parties. Your information stays with us.</p>
          <h2>PDF Protection</h2>
          <p>All PDFs are password-protected and linked to your email address. We use DRM measures to prevent unauthorized sharing.</p>
          <h2>Contact</h2>
          <p>For any privacy-related concerns, email us at support@shortly.life.</p>
        </div>
      </div>
    </div>
  );
}

// ── Terms ─────────────────────────────────────────────────────────────────────
export function TermsPage() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="container">
          <p className={styles.eyebrow}>Terms of Service</p>
          <h1 className={styles.heading}>Terms & Conditions</h1>
        </div>
      </div>
      <div className="container section-gap-sm">
        <div className={styles.prose}>
          <h2>Acceptance of Terms</h2>
          <p>By using Shortly Life, you agree to these terms and conditions. If you do not agree, please do not use our services.</p>
          <h2>Course Access</h2>
          <p>Upon purchase, you receive a single-user license to use the PDF for personal, non-commercial purposes. Sharing, distributing, or reselling the PDF is strictly prohibited.</p>
          <h2>Intellectual Property</h2>
          <p>All course content is owned by Shortly Life and protected by copyright law. Reproduction in any form without written permission is prohibited.</p>
          <h2>Limitation of Liability</h2>
          <p>Shortly Life is not liable for any indirect, incidental, or consequential damages arising from the use of our courses.</p>
          <h2>Changes to Terms</h2>
          <p>We may update these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms.</p>
        </div>
      </div>
    </div>
  );
}